import { Box, useMediaQuery } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateRange, PickersShortcutsItem, StaticDateRangePicker } from "@mui/x-date-pickers-pro";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { DateTime } from "luxon";
import styles from "./DateRangePicker.module.scss";
import { useState, useRef, MouseEvent, useEffect } from "react";
import calendarIcon from "../calendar/assets/calendar.svg";

const defaultShortcutItems: PickersShortcutsItem<DateRange<DateTime>>[] = [
  {
    label: "This Week",
    getValue: () => {
      const today = DateTime.local();
      return [DateTime.now(), today.endOf("week")];
    },
  },
  {
    label: "Next Week",
    getValue: () => {
      const today = DateTime.local();
      const nextWeek = today.plus({ weeks: 1 });
      return [nextWeek.startOf("week"), nextWeek.endOf("week")];
    },
  },
  {
    label: "Next 7 Days",
    getValue: () => {
      const today = DateTime.local();
      return [today.plus({ days: 7 }), today];
    },
  },
  {
    label: "Current Month",
    getValue: () => {
      const today = DateTime.local();
      return [today.startOf("month"), today.endOf("month")];
    },
  },
  { label: "Reset", getValue: () => [null, null] },
];

interface DateRangePickerProps {
  dateRange: DateRange<DateTime<boolean>>;
  onChange: (dateRange: DateRange<DateTime<boolean>>) => void;
  responsive?: boolean;
  className?: string;
  iconAtLeft?: boolean;
  style?: React.CSSProperties;
  disabled?: boolean;
  dropdownTop?: boolean;
  dropdownAlignment?: "left" | "right";
  placeholder?: string;
  shortcutItems?: PickersShortcutsItem<DateRange<DateTime>>[];
}

const DateRangePicker = ({
  dateRange,
  onChange,
  responsive,
  className,
  iconAtLeft,
  style,
  disabled,
  dropdownTop,
  dropdownAlignment,
  placeholder,
  shortcutItems,
}: DateRangePickerProps) => {
  const [showDropdown, setShowDropdown] = useState(false);

  const dropDownRef = useRef<HTMLDivElement>(null);
  const selectRef = useRef<HTMLDivElement>(null);

  const isLargeScreen = useMediaQuery("(min-width: 768px)");

  const handleClose = (e: globalThis.MouseEvent) => {
    const dropdown = dropDownRef.current;

    if (!dropdown || !dropdown.contains(e.target as Element)) {
      setShowDropdown(false);
    }
  };

  useEffect(() => {
    window.addEventListener("click", handleClose);
    return () => window.removeEventListener("click", handleClose);
  }, []);

  const handleSelectClick = (e: MouseEvent<HTMLDivElement>) => {
    if (disabled) {
      return;
    }

    const select = selectRef.current;
    if (select && select.contains(e.target as Element)) {
      setShowDropdown(!showDropdown);
    }
  };

  const handleDateSelect = (dateRange: DateRange<DateTime<boolean>>) => {
    onChange?.(dateRange);
    if ((dateRange?.[0] && dateRange?.[1]) || (!dateRange?.[0] && !dateRange?.[1])) {
      setShowDropdown(false);
    }
  };

  const shownValue =
    dateRange[0] || dateRange[1]
      ? `${dateRange[0]?.toLocaleString({ month: "short", day: "numeric", year: "numeric" }) || ""} - ${
          dateRange[1]?.toLocaleString({ month: "short", day: "numeric", year: "numeric" }) || ""
        }`
      : "";

  return (
    <div
      className={[
        styles["select-wrapper"],
        showDropdown ? styles.active : "",
        responsive && styles.responsive,
        className,
      ].join(" ")}
      ref={dropDownRef}
      onClick={handleSelectClick}
      style={style}
    >
      <div className={[styles["main-content"], iconAtLeft && styles["icon-at-left"]].join(" ")} ref={selectRef}>
        {
          <>
            {iconAtLeft && <img alt="calendar" className={styles.icon} src={calendarIcon} />}
            {shownValue ? (
              <span className={styles["main-text"]}>{shownValue}</span>
            ) : (
              <span className={styles.placeholder}>{placeholder || "Select date"}</span>
            )}
            {!iconAtLeft && <img alt="calendar" className={styles.icon} src={calendarIcon} />}
          </>
        }
      </div>
      <div
        className={[
          styles.dropdown,
          showDropdown && styles["show-dropdown"],
          styles[dropdownAlignment || "left"],
          dropdownTop && styles["on-top"],
        ].join(" ")}
      >
        <LocalizationProvider dateAdapter={AdapterLuxon}>
          <StaticDateRangePicker
            slotProps={{
              shortcuts: isLargeScreen ? { items: shortcutItems || defaultShortcutItems } : undefined,
              actionBar: { actions: [] },
            }}
            calendars={1}
            onChange={handleDateSelect}
            sx={{
              "@media (max-width: 768px)": {
                display: "flex",
                flexDirection: "column",
                marginLeft: "-20px",
              },
              backgroundColor: "rgb(26, 30, 54)",
            }}
          />
        </LocalizationProvider>
      </div>
    </div>
  );
};

export default DateRangePicker;
